<template>
  <div class="gansu-map-wrapper">
    <div class="gansu-map">
      <!-- 地图容器 -->
      <div id="gansu-map-container"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, shallowRef, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import AMapLoader from '@amap/amap-jsapi-loader'

// 地图实例引用
const map = shallowRef(null)
// 存储地图上的多边形覆盖物
const polygons = ref([])
// 存储地图上的标记点
const markers = ref([])
// 地图数据
const mapData = ref(null)

// 初始化地图
const drawMap = async () => {
  try {
    // 等待DOM更新完成，确保容器尺寸已经确定
    await nextTick()

    const AMap = await AMapLoader.load({
      key: '6588d9e5b6ae0751c799cb4c7b3e30be', // 使用项目中已有的高德地图API Key
      version: '2.0',
      plugins: ['AMap.MouseTool', 'AMap.PolyEditor', 'AMap.Object3DLayer']
    })

    // 创建地图实例 - 启用3D视图
    map.value = new AMap.Map('gansu-map-container', {
      zoom: 6, // 初始缩放级别
      mapStyle: 'amap://styles/whitesmoke',
      center: [103.8349, 36.0580], // 甘肃省中心点
      viewMode: '3D', // 启用3D视图
      pitch: 50, // 降低俯仰角，使可视范围更大，能够显示完整的甘肃省区域
      rotation: 0, // 设置旋转角
      // 3D地图特有配置
      skyColor: '#000019', // 天空颜色
      fog: {
        color: 'rgba(0, 0, 25, 0.8)', // 雾的颜色
        near: 5000, // 调整雾的最小距离，优化显示效果
        far: 10000 // 调整雾的最大距离，优化显示效果
      }
    })

    map.value.setZooms([2, 20])
    
    // 启用3D地图交互控制
    map.value.plugin(['AMap.ControlBar', 'AMap.MouseTool'], () => {
      // 添加控制栏，可控制地图旋转、倾斜等
      const controlBar = new AMap.ControlBar({
        position: 'RB', // 控制栏位置
        showZoomBar: true,
        showControlButton: true,
        autoPosition: true
      })
      map.value.add(controlBar)
    })

    // 添加地图加载完成事件
    map.value.on('complete', () => {
      console.log('甘肃省3D地图加载完成')
      // 开启3D建筑物显示
      map.value.plugin(['AMap.Buildings'], () => {
        // 添加3D建筑物图层
        const buildings = new AMap.Buildings({
          zooms: [10, 20],
          heightFactor: 2 // 3D建筑高度缩放比例
        })
        map.value.add(buildings)
      })
      
      // 地图加载完成后加载边界数据
      loadGansuData()
    })

    // 监听窗口大小变化，自动调整地图尺寸
    const handleResize = () => {
      if (map.value) {
        map.value.resize()
      }
    }

    window.addEventListener('resize', handleResize)

    // 在组件销毁时移除事件监听
    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize)
    })

  } catch (error) {
    console.error('地图加载失败:', error)
  }
}

// 加载甘肃省地图数据
const loadGansuData = async () => {
  try {
    // 加载gansu.json文件
    const response = await fetch('/gansu.json')
    const data = await response.json()
    mapData.value = data
    
    // 绘制地图边界
    drawGansuBoundaries(data)
  } catch (error) {
    console.error('加载甘肃省地图数据失败:', error)
  }
}

// 绘制甘肃省边界和各市边界
const drawGansuBoundaries = (data) => {
  if (!map.value || !data) return

  // 清除现有覆盖物
  clearMap()

  polygons.value = []
  markers.value = []

  // 遍历地图数据中的每个城市/区域
  if (data.features && data.features.length > 0) {
    data.features.forEach(feature => {
      // 处理多边形数据
      if (feature.geometry && feature.geometry.type === 'Polygon') {
        const polygon = createPolygon(feature)
        polygons.value.push(polygon)
      } else if (feature.geometry && feature.geometry.type === 'MultiPolygon') {
        feature.geometry.coordinates.forEach(coordinates => {
          const polygonFeature = {
            ...feature,
            geometry: {
              type: 'Polygon',
              coordinates: coordinates
            }
          }
          const polygon = createPolygon(polygonFeature)
          polygons.value.push(polygon)
        })
      }
      
      // 创建城市中心点标记
      const marker = createCityMarker(feature.properties)
      if (marker) {
        markers.value.push(marker)
      }
    })

    // 添加所有多边形和标记到地图
    const allFeatures = [...polygons.value, ...markers.value]
    if (allFeatures.length > 0) {
      map.value.add(allFeatures)
      // 调整地图视野，最大化显示甘肃省区域，设置合理的边距避免元素紧贴边缘
      map.value.setFitView(
        allFeatures, 
        false, // 不考虑地图控件
        [40, 40, 40, 40] // 上右下左边距，单位为像素
      )
      
      // 设置期望的缩放级别，确保地图达到合适的放大效果
      // 注意：这里的缩放级别设置在setFitView之后，这样可以先确保完整显示，再进行放大
      map.value.setZoom(6)
    }
  }
}

// 创建多边形
const createPolygon = (feature) => {
  // 处理坐标数据格式
  let coordinates = feature.geometry.coordinates
  // 如果是多层嵌套的数组，提取最内层坐标
  if (Array.isArray(coordinates[0]) && Array.isArray(coordinates[0][0]) && Array.isArray(coordinates[0][0][0])) {
    coordinates = coordinates[0]
  }

  // 城市属性信息
  const properties = feature.properties || {}
  
  // 创建多边形 - 只保留边界线，不使用填充颜色，边界线使用灰色
  const polygon = new AMap.Polygon({
    path: coordinates,
    zIndex: 50,
    bubble: true,
    strokeColor: '#999999', // 将边界线颜色改为灰色
    strokeOpacity: 0.7,
    strokeWeight: 1,
    fillOpacity: 0, // 设置填充透明度为0，不显示区域颜色
    strokeStyle: "solid"
  })

  return polygon
}

// 创建城市中心点标记
const createCityMarker = (properties) => {
  if (!properties.center || properties.center.length !== 2) return null
  
  // 创建自定义HTML内容
  const content = document.createElement('div')
  content.style.position = 'relative'
  content.style.width = '130px'
  content.style.height = '120px'  // 增加容器高度以适应竖线
  
  // 创建竖线 - 从下往上延伸，确保底部中心点与坐标精确对齐
  const lineElement = document.createElement('div')
  lineElement.style.position = 'absolute'
  lineElement.style.left = '5px'   // 居中显示竖线
  lineElement.style.bottom = '0px' // 竖线底部与容器底部对齐
  lineElement.style.width = '2px'
  lineElement.style.height = '120px'
  lineElement.style.backgroundColor = '#FFD700'
  lineElement.style.borderRadius = '1.5px'
  lineElement.style.boxShadow = '0 0 8px rgba(255, 215, 0, 0.6)'
  
  // 创建信息区域 - 与竖线顶部对齐
  const infoElement = document.createElement('div')
  infoElement.style.position = 'absolute'
  infoElement.style.left = '12px'  // 信息区域与竖线有间距
  infoElement.style.top = '0px'    // 信息区域顶部与竖线顶部对齐
  infoElement.style.width = '110px'
  infoElement.style.height = '70px'
  // 远山黛浅色背景 - 淡青色系，增加透明度使其更加通透
  infoElement.style.backgroundColor = 'rgba(169, 186, 190, 0.7)'
  infoElement.style.padding = '6px'
  infoElement.style.borderRadius = '4px'
  // 深色文字以确保在浅色背景上的可读性
  infoElement.style.color = '#1A237E'
  infoElement.style.fontSize = '12px'
  infoElement.style.lineHeight = '1.5'
  infoElement.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.2)'
  // 浅色边框与背景协调
  infoElement.style.border = '1px solid rgba(140, 160, 165, 0.5)'
  
  // 填充信息内容
  const cityName = properties.name || '未知城市'
  const vehicleCount = properties.vehicleCount || 0
  const equipmentCount = properties.equipmentCount || 0
  
  infoElement.innerHTML = `
    <div style="font-weight: bold; color: #FFD700; font-size: 12px; margin-bottom: 2px;">${cityName}</div>
    <div style="display: flex; justify-content: space-between;">
      <span>智能网联车辆:</span>
      <span style="color: #4CAF50; font-weight: bold;">${vehicleCount}</span>
    </div>
    <div style="display: flex; justify-content: space-between;">
      <span>无人驾驶车辆:</span>
      <span style="color: #4CAF50; font-weight: bold;">${equipmentCount}</span>
    </div>
  `
  
  // 将子元素添加到主容器
  content.appendChild(lineElement)
  content.appendChild(infoElement)
  
  // 创建标记点
  // 关键修改：将锚点设置为竖线底部的中心点，确保无论缩放如何，竖线底部都精确对准城市中心
  const marker = new AMap.Marker({
    position: properties.center,
    content: content,
    title: cityName,
    zIndex: 150,
    // 修改锚点：使竖线底部中心点精确对齐城市坐标点
    // x: 竖线左侧位置 + 竖线宽度的一半 = 5px + 1.5px = 6.5px
    // y: 容器高度 = 120px（因为竖线底部与容器底部对齐）
    offset: new AMap.Pixel(-6.5, -120) // 确保y值等于容器高度，使竖线底部精确对齐城市坐标
  })
  
  return marker
}

// 清除地图上的所有覆盖物
const clearMap = () => {
  if (polygons.value && polygons.value.length > 0 && map.value) {
    map.value.remove(polygons.value)
    polygons.value = []
  }
  // 清除标记点
  if (markers.value && markers.value.length > 0 && map.value) {
    map.value.remove(markers.value)
    markers.value = []
  }
}

// 销毁地图
const destroyMap = () => {
  if (map.value) {
    map.value.destroy()
    map.value = null
    console.info("甘肃省地图已销毁")
  }
}

// 生命周期钩子
onMounted(async () => {
  await drawMap()
})

onBeforeUnmount(() => {
  destroyMap()
})
</script>

<style lang="less" scoped>
.gansu-map-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 600px; /* 最小高度，确保地图在小屏幕上也有合适的显示区域 */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.gansu-map {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
}

#gansu-map-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: all 0.5s ease;
}



/* 隐藏高德地图logo和版权信息 */
/deep/ .amap-logo {
  display: none !important;
}

/deep/ .amap-copyright {
  opacity: 0 !important;
}

/* 响应式布局 */
@media screen and (max-width: 768px) {
  .gansu-map-wrapper {
    min-height: 400px;
  }
  
  .city-info-panel {
    width: calc(100% - 40px); /* 移动端适配 */
    max-width: 320px;
    bottom: 15px;
    right: 15px;
    left: 15px;
    padding: 12px;
  }
  
  .panel-title {
    font-size: 15px;
  }
  
  .panel-content {
    font-size: 13px;
  }
}

@media screen and (max-width: 480px) {
  .city-info-panel {
    bottom: 10px;
    right: 10px;
    left: 10px;
    width: calc(100% - 20px);
  }
}
</style>